import styled, { css } from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { isHorizontal, isVertical, selectTabsPanelTheme } from '../utils';
import { ITabsPanelProps, TabOrientation, TabStyle } from '../tab-types';
import { ContentLayer } from '../../layers';

interface ITabContentWrapperProps extends Pick<ITabsPanelProps, 'remoteHeaderContainerRef'> {
    tabStyle: TabStyle;
    tabOrientation: TabOrientation;
    tabOverflow: boolean;
}

const getWidth = memoizeFunction((tabOrientation: TabOrientation, remoteHeaderContainerRef: React.RefObject<HTMLElement>) =>
    ((isHorizontal(tabOrientation) || remoteHeaderContainerRef) ? '100%' : 'calc(100% - 150px)'));

const getDisplayPropValue = memoizeFunction((tabOrientation: TabOrientation) => (isHorizontal(tabOrientation) ? 'block' : 'inline-block'));

const getTopPosition = memoizeFunction((tabOrientation: TabOrientation) => (isHorizontal(tabOrientation) ? '-1px' : '0px'));

const getRightPosition = memoizeFunction((tabOrientation: TabOrientation, remoteHeaderContainerRef: React.RefObject<HTMLElement>) =>
    ((isHorizontal(tabOrientation) || remoteHeaderContainerRef) ? '0px' : '1px'));

const contentPanelLineBorderRadius = css<ITabContentWrapperProps>`
    border-radius: ${selectTabsPanelTheme((t, _isLine, { tabOrientation }) =>
    (isVertical(tabOrientation) ?
        `0 ${t.composites.borderRadius.content} ${t.composites.borderRadius.content} 0` :
        `0 0 ${t.composites.borderRadius.content} ${t.composites.borderRadius.content}`))};
`;

const contentPanelConventionalBorderRadius = css<ITabContentWrapperProps>`
    border-radius: ${selectTabsPanelTheme((t, _isLine, { tabOrientation, tabOverflow }) =>
    (isVertical(tabOrientation) ?
        `0 ${t.composites.borderRadius.content} ${t.composites.borderRadius.content} ${tabOverflow ? 0 : t.composites.borderRadius.content}` :
        `0 ${tabOverflow ? 0 : t.composites.borderRadius.content} ${t.composites.borderRadius.content} ${t.composites.borderRadius.content}`))};
`;

export const TabContentContainer = styled(ContentLayer).attrs(() => ({
    className: String('ap-tabsPanel-tabContentContainer')
})) <ITabContentWrapperProps>`
    height: 100%;
    overflow-y: auto;
    box-sizing: border-box;
    outline: none;
    width: ${({ tabOrientation, remoteHeaderContainerRef }) => getWidth(tabOrientation, remoteHeaderContainerRef!)};
    display: ${({ tabOrientation }) => getDisplayPropValue(tabOrientation)};
    top: ${({ tabOrientation }) => getTopPosition(tabOrientation)};
    right: ${({ tabOrientation, remoteHeaderContainerRef }) => getRightPosition(tabOrientation, remoteHeaderContainerRef!)};
    border: ${selectTabsPanelTheme(t => `1px solid ${t.colors.tabContentBorder}`)};
    ${selectTabsPanelTheme((t, isLine) =>
    (isLine ? contentPanelLineBorderRadius : contentPanelConventionalBorderRadius))};
    position: ${({ tabOrientation }) => (isHorizontal(tabOrientation) ? 'relative' : 'absolute')};
`;
